// ─────────────────────────────────────────────
//  stampTime — reading a recorded clinical time, whatever shape it arrived in.
//
//  ⚠️ THIS EXISTS BECAUSE THE BOARD AND THE PHONE READ THE SAME DOCUMENT AND
//  DISAGREED ABOUT IT. `or_cases.phases[id].at` comes back as a Firestore
//  Timestamp from a live listener, as a plain Date from a local write that has
//  not round-tripped, as `{seconds}` from a cached or serialised snapshot, and
//  as an "HH:MM" string on older rows. The mobile Room Status screen coerced all
//  of those; the board's copy did `new Date(value)`, which is Invalid Date for a
//  Timestamp object — so on 2026-09-07 the phone showed four phases stamped at
//  22:08 and the board card showed the same four as never stamped at all.
//
//  A clinician looking at both surfaces in one shift was being told two
//  different things about one case. So the coercion is written ONCE, here, and
//  both surfaces import it: copied logic survives, copied vocabulary drifts.
//
//  ⚠️ TOTAL, AND SILENT ON FAILURE. Every function returns null or "" rather
//  than throwing or printing "Invalid Date" onto a clinical card. A time we
//  cannot read is a time we do not claim to know — the caller renders the phase
//  name alone, which is honest, instead of an hour nobody recorded.
// ─────────────────────────────────────────────

// Already-formatted wall clock ("07:40", "22:08"). Recognised BEFORE any Date
// coercion, because `new Date("07:40")` is Invalid Date in every engine and
// coercing it would blank a real stamp.
const CLOCK_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

export const isClockString = (v) => typeof v === "string" && CLOCK_RE.test(v);

/**
 * Anything a stamp can be → a Date, or null.
 *
 * Order matters: the cheapest and most specific checks first, and the string
 * branch last so a clock string never reaches `new Date`.
 */
export function toStampDate(value) {
  if (value == null) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  // A live Firestore Timestamp. Guarded: a hostile or half-deserialised object
  // can carry a `toDate` that throws, and this runs while a board is rendering.
  if (typeof value.toDate === "function") {
    try {
      const d = value.toDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
    } catch {
      return null;
    }
  }
  // A serialised Timestamp — what a cached snapshot and our own local store hold.
  if (typeof value === "object" && Number.isFinite(value.seconds)) {
    return new Date(value.seconds * 1000);
  }
  if (typeof value === "number" && Number.isFinite(value)) return new Date(value);
  if (typeof value === "string") {
    if (CLOCK_RE.test(value)) return null; // a wall clock has no date to be
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/**
 * The 24-hour wall clock both Room Status and the board card print — "22:08".
 *
 * ⚠️ ONE SPELLING ACROSS BOTH SURFACES. The phone and the room card show the
 * same stamp side by side on the same day; "22:08" here and "10:08 PM" there
 * reads as two different facts to somebody checking one against the other.
 */
export function formatClock(value) {
  if (isClockString(value)) return value;
  const d = toStampDate(value);
  if (!d) return "";
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/**
 * The 12-hour spelling, for PROSE — a push notification or a message body,
 * where "at 9:41 PM" is how a sentence reads and "at 21:41" is not.
 *
 * Deliberately a second function rather than a flag: the choice is about where
 * the string is going, and a caller should have to say which it means.
 */
export function formatClock12(value) {
  const d = toStampDate(value) || (isClockString(value) ? clockToDate(value) : null);
  if (!d) return "";
  let h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, "0");
  const suffix = h >= 12 ? "PM" : "AM";
  h %= 12;
  if (h === 0) h = 12;
  return `${h}:${m} ${suffix}`;
}

// A wall-clock string carries no date, so it is anchored to today purely to be
// re-spelled. Never returned to a caller as a moment in time.
function clockToDate(value) {
  const [h, m] = value.split(":").map(Number);
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d;
}
